import { TaskStatus, TaskTemplate } from '../../shared/types/Task';

/**
 * Calculates the escalation date for a given task based on its status, template, and SLA rules.
 * Returns a formatted MM/DD/YYYY string or an empty string if not applicable.
 *
 * @param {object} task - The task object
 * @param {Array} taskTemplates - Array of task template objects
 * @returns {string} Escalation date in MM/DD/YYYY format or ''
 */
export const getEscalationDate = (
  task: any,
  taskTemplates: TaskTemplate[] | undefined
): string => {
  // Return empty if task is not started, templates missing, or task is pending or complete
  if (
    !taskTemplates ||
    task.status === TaskStatus.complete ||
    task.status === TaskStatus.pending
  )
    return '';

  // Find the matching template for the task
  const template = taskTemplates.find((tpl) => task.taskTemplateID === tpl.id);
  if (!template) return '';

  // Extract template values
  const taskAge = Number(template.taskAge) || 0;
  const templateReview = !!template.review;
  let stakeholderSla1: number | null = null;

  // Parse stakeholder SLA if present and template requires review
  if (templateReview && template.stakeholderSla) {
    try {
      const slaObj =
        typeof template.stakeholderSla === 'string'
          ? JSON.parse(template.stakeholderSla)
          : template.stakeholderSla;
      stakeholderSla1 = slaObj?.stakeholderSla1
        ? Number(slaObj.stakeholderSla1)
        : null;
    } catch {
      stakeholderSla1 = null;
    }
  }

  // Determine escalation days based on task status and template rules
  let escalationDays = taskAge;
  if (task.status === TaskStatus.todo && task.review === 'false') {
    escalationDays = taskAge;
  } else if (task.status === TaskStatus.inReview && !templateReview) {
    escalationDays = taskAge;
  } else if (
    task.status === TaskStatus.inReview &&
    templateReview &&
    stakeholderSla1
  ) {
    escalationDays = stakeholderSla1;
  } else if (task.status === TaskStatus.changesRequested && templateReview) {
    escalationDays = taskAge;
  }

  // If escalationDays is not set or zero, return empty
  if (!escalationDays) return '';

  // Guard: no activities means no base date to calculate from
  if (!task.activities?.length) return '';

  // activities are ordered DESC by the API, so index 0 is the most recent
  const baseDate = new Date(task.activities[0].updatedAt);
  if (isNaN(baseDate.getTime())) return '';
  baseDate.setDate(baseDate.getDate() + escalationDays);

  // Format date as MM/DD/YYYY
  const month = String(baseDate.getMonth() + 1).padStart(2, '0');
  const day = String(baseDate.getDate()).padStart(2, '0');
  const year = baseDate.getFullYear();
  return `${month}/${day}/${year}`;
};
